import React from 'react'
import { useTranslation } from 'react-i18next'
import { SelectedSlot } from '@customTypes/Availabilities'
import { useConfig } from '@context/ConfigurationContext'
import { useContactDetailsState } from '@context/ContactDetailsContext'
import useClinic from '@hooks/useClinic'
import { CalendarButton } from '../CalendarButton/CalendarButton'
import useStyles from './styles'
import { TEST_IDS } from 'src/constants/testIds'
import SuccessAnimation from '@components/SuccessAnimation'
import { AppointmentSummary } from '@components/AppointmentSummary/AppointmentSummary'
import { useVerifyPhoneContext } from '@context/VerifyPhoneContext'
import { BookingFor } from '@customTypes/BookingFor'
import { useChoiceState } from '@context/ChoiceContext'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { ActionButtons } from '../ActionButtons/ActionButtons'

interface SuccessScreenProps {
  appointmentDuration?: number
  selectedBookableDateAndTime?: SelectedSlot
  openAna: () => void
}

export const SuccessScreenBody = ({
  appointmentDuration,
  selectedBookableDateAndTime,
  openAna,
}: SuccessScreenProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { clinicId, linkToAna, hasHealthAssistant, hasValidAnamnesis, healthAssistantLink, isInternalClinic } =
    useConfig()
  const { data: clinic } = useClinic(selectedBookableDateAndTime?.clinicId || clinicId)
  const { firstName, lastName, bookingFor, patient_firstName, patient_lastName } = useContactDetailsState()
  const { patientType } = useChoiceState()
  const { selectedPatient } = useVerifyPhoneContext()
  const { street, houseNumber, city, zipCode, officialName } = clinic || {}
  const appointmentDate = new Date(
    `${selectedBookableDateAndTime?.date || ''}T${selectedBookableDateAndTime?.time || ''}`
  )

  const patientName = (() => {
    if (patientType === PatientTypeOptions.OLD && selectedPatient?.fullName) {
      return selectedPatient.fullName
    }

    if (bookingFor === BookingFor.BOOKING_FOR_ME && firstName && lastName) {
      return `${firstName} ${lastName}`
    }

    if (patient_firstName && patient_lastName) {
      return `${patient_firstName} ${patient_lastName}`
    }

    return undefined
  })()

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <SuccessAnimation />
        <div className={classes.title} data-test={TEST_IDS.successTitle}>
          {t('booking_success_screen_title')}
        </div>
        <AppointmentSummary patientFullName={patientName} />
        <div className={classes.calendarButtonContainer}>
          <CalendarButton
            event={{
              title: t('calendar_event_summary', { clinicName: clinic?.officialName }),
              description: t('calendar_event_description'),
              start: appointmentDate,
              location: `${officialName},  ${street} ${houseNumber}, ${zipCode} ${city}`,
              duration: [appointmentDuration || 45, 'minutes'],
            }}
          />
        </div>

        {isInternalClinic && (
          <ActionButtons
            openAna={openAna}
            linkToAna={linkToAna}
            hasHealthAssistant={hasHealthAssistant}
            hasValidAnamnesis={hasValidAnamnesis}
            healthAssistantLink={healthAssistantLink}
            patientType={patientType}
          />
        )}
      </div>
    </div>
  )
}
